/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import { call, delay, put, race } from "redux-saga/effects";

import * as accessControlApi from "Services/AccessControlApi";

import {
  COUNTRY_REGION,
  REGION,
  ROLE,
  STATE_PROVINCE,
  TECHNOLOGY_AREA,
  USER_STATUS,
} from "Constants/AccessControlConstants";
import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { IAccessControlDataResult } from "Interfaces/AccessControl/IAccessControlDataResult";
import { IAccessControlRequest } from "Interfaces/AccessControl/IAccessControlRequest";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";
import { IWriteAccessControlUser } from "Interfaces/AccessControl/IWriteAccessControlUser";
import { IWriteUpdateUserStatus } from "Interfaces/AccessControl/IWriteUpdateUserStatus";
import { IApiErrorResult, IApiResult, IBadRequestResult } from "Interfaces/IApiResult";
import { ICreateUserResult } from "Services/Interfaces/AccessControl/ICreateUserResult";
import { ICreateUserRoleResult } from "Services/Interfaces/AccessControl/ICreateUserRoleResult";
import { IUpdateUserStatusResult } from "Services/Interfaces/AccessControl/IUpdateUserStatusResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  createUserFail,
  createUserRoleFail,
  createUserRoleSuccess,
  createUserSuccess,
  deleteUserRoleFail,
  deleteUserRoleSuccess,
  loadUserRolesFail,
  loadUserRolesSuccess,
  searchUsersFail,
  searchUsersSuccess,
  updateUserRoleFail,
  updateUserRoleSuccess,
  updateUserStatusFail,
  updateUserStatusSuccess,
  ICreateUser,
  ICreateUserRole,
  IDeleteUserRole,
  ILoadUserRoles,
  ISearchUsers,
  IUpdateUserRole,
  IUpdateUserStatus,
} from "./AccessControlAction";

/**
 * Calls request to create an access user
 * @param {ICreateUser} action triggering action
 * @returns {Generator} resulting function generated
 */
function* handleCreateUser(action: ICreateUser): Generator {
  const failedMessage = "Failed to create the user";

  try {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const { result, timeout }: IApiResult<ICreateUserResult> | any = yield race({
      result: call(accessControlApi.createUser, action.tenant, action.accessUser),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(createUserFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        const okResult = result.parsedBody as IWriteAccessControlUser;

        yield put(createUserSuccess(okResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(createUserFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.ServerError:
        yield put(createUserFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(createUserFail(failedMessage));
        break;
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(createUserFail(String(error.message)));
    }
  }
}

/**
 * Calls request to get the load user roles.
 * @param {ILoadUserRoles} action triggering action
 * @returns {Generator} resulting function generated
 */
function* handleLoadUserRoles(action: ILoadUserRoles): Generator {
  const failedMessage = "Failed to load user roles";

  try {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const { result, timeout }: IApiResult<IUserRole[]> | any = yield race({
      result: call(accessControlApi.loadUserRoles, action.tenant, action.email),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadUserRolesFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const okResult = result.parsedBody as IUserRole[];

        yield put(loadUserRolesSuccess(okResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(loadUserRolesFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.ServerError:
        yield put(loadUserRolesFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(loadUserRolesFail(failedMessage));
        break;
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(loadUserRolesFail(String(error.message)));
    }
  }
}

/**
 * Calls request to delete an user role.
 * @param {IDeleteUserRole} action triggering action
 * @returns {Generator} resulting function generated
 */
function* handleDeleteUserRole(action: IDeleteUserRole): Generator {
  const failedMessage = "Failed to delete user role";

  try {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const { result, timeout }: IApiResult<IUserRole[]> | any = yield race({
      result: call(accessControlApi.deleteUserRole, action.roleId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(deleteUserRoleFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(deleteUserRoleSuccess(action.roleId));
        break;
      case HttpStatusCode.BadRequest:
        yield put(deleteUserRoleFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.ServerError:
        yield put(deleteUserRoleFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(deleteUserRoleFail(failedMessage));
        break;
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(deleteUserRoleFail(String(error.message)));
    }
  }
}

/**
 * Calls request to create an user role.
 * @param {ICreateUserRole} action triggering action
 * @returns {Generator} resulting function generated
 */
function* handleCreateUserRole(action: ICreateUserRole): Generator {
  const failedMessage = "Failed to create user role";

  try {
    const { result, timeout }: IApiResult<ICreateUserRoleResult> | any = yield race({
      result: call(accessControlApi.createUserRole, action.tenant, action.role),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(createUserRoleFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        const okResult = result.parsedBody as IUserRole;
        yield put(createUserRoleSuccess(okResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(createUserRoleFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.ServerError:
        yield put(createUserRoleFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(createUserRoleFail(failedMessage));
        break;
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(createUserRoleFail(String(error.message)));
    }
  }
}

/**
 * Calls request to update an user role.
 * @param {ICreateUserRole} action triggering action
 * @returns {Generator} resulting function generated
 */
function* handleUpdateUserRole(action: IUpdateUserRole): Generator {
  const failedMessage = "Failed to update user role";

  try {
    const { result, timeout }: IApiResult<IUpdateUserStatusResult> | any = yield race({
      result: call(accessControlApi.updateUserRole, action.tenant, action.role),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(createUserRoleFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const okResult = result.parsedBody as IUserRole;
        yield put(updateUserRoleSuccess(okResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateUserRoleFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      case HttpStatusCode.ServerError:
        yield put(updateUserRoleFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateUserRoleFail(failedMessage));
        break;
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(updateUserRoleFail(String(error.message)));
    }
  }
}

/**
 * Calls request to update the user status
 * @param {IUpdateUserStatus} action triggering action
 * @returns {Generator} resulting function generated
 */
function* handleUpdateUserStatus(action: IUpdateUserStatus): Generator {
  const failedMessage = "Failed to update the user status";

  const userStatus: IWriteUpdateUserStatus = {
    userId: action.userId,
    isActive: action.isActive,
  };

  try {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const { result, timeout }: IApiResult<IUpdateUserStatusResult> | any = yield race({
      result: call(accessControlApi.updateUserStatus, action.tenant, userStatus),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateUserStatusFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }
    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const okResult = result.parsedBody as IWriteUpdateUserStatus;
        yield put(updateUserStatusSuccess(okResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateUserStatusFail((result.parsedBody as IBadRequestResult).message));
        break;
      case HttpStatusCode.ServerError:
        yield put(updateUserStatusFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateUserStatusFail(failedMessage));
        break;
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(updateUserStatusFail(String(error.message)));
    }
  }
}

/**
 * Search all access users for given text
 * @param {ISearchUsers} action for searching admin users
 * @returns {void}
 */
function* handleSearchAccessUsers(action: ISearchUsers) {
  const failedMessage = "Failed to search the access users.";
  const requestBody = {
    searchKey: action.searchText,
    usersStatus: [],
    usersRole: [],
    usersRegion: [],
    usersCountry: [],
    usersState: [],
    usersTechArea: [],
    pageIndex: action.searchPage,
    pageSize: action.pageSize,
    tenantName: action.tenant,
  } as unknown as IAccessControlRequest;

  action.selectedAccessControlFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case USER_STATUS:
        requestBody.usersStatus.push(item.value);
        break;
      case COUNTRY_REGION:
        requestBody.usersCountry.push(item.value);
        break;
      case STATE_PROVINCE:
        requestBody.usersState.push(item.value);
        break;
      case ROLE:
        requestBody.usersRole.push(item.value);
        break;
      case REGION:
        requestBody.usersRegion.push(item.value);
        break;
      case TECHNOLOGY_AREA:
        requestBody.usersTechArea.push(item.value.split("__")[0]);
        break;
      default:
        return;
    }
  });

  try {
    const { result, timeout }: IApiResult<IAccessControlDataResult> | any = yield race({
      result: call(accessControlApi.searchAccessControlList, requestBody.tenantName, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchUsersFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchUsersSuccess(
            parsedBody.accessControlAdminList,
            action.searchPage,
            action.pageSize,
            parsedBody.filteredCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchUsersFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchUsersFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchUsersFail(error));
  }
}

export {
  handleCreateUser,
  handleUpdateUserStatus,
  handleLoadUserRoles,
  handleDeleteUserRole,
  handleCreateUserRole,
  handleUpdateUserRole,
  handleSearchAccessUsers,
};
